import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelectedContract } from 'hooks'
import { MenuTitles, Pages } from 'constants/menu'
import { ContractSubPage } from '../ContractSubPage'
import { ErrorModal } from 'helpers/ErrorModal'
import { isDirtyEqual } from 'utils/compares'
import { getContractInformationSubPages } from './ContractInformationSubPages'
import { SubPagePanel } from 'components/panel/SubPagePanel'
import {
  FormCondition,
  FormConditionCondition,
  SignboardRemoval,
  SignboardRemovalRemovalType,
  StaffUpdateContractSettlementRequest,
  useInvalidateUseStaffGetContractSettlement,
  useStaffGetContractSettlement,
  useStaffUpdateContractSettlement,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxDatePicker,
  AgxDivider,
  AgxRadioGroup,
  AgxSpacer,
  AgxTextInput,
} from '@urbanx/agx.ui-components2'
import dayjs from 'dayjs'
import LoadingState from 'components/loadingState/LoadingState'
import ConditionDate from './components/ConditionDate'
import { EditConditionModal } from './modals/EditConditionModal'
import { useFormConditionDueDate } from './components/useFormConditionDueDate'
import RexDisconnectedModal from 'pages/campaigns/components/manageCampaign/RexDisconnectedModal'
import { RexDisconnectedModalFields, RexDisconnectedModalFieldValue } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper'
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client'
import { isEqual } from 'lodash-es'
import { useGetSpecialConditionsInfo } from 'hooks/useGetSpecialConditionsInfo'

const Settlement = () => {
  const { contract, campaignId } = useSelectedContract()
  const [isLoaded, setIsLoaded] = useState(false)
  const [validate, setValidate] = useState(false)
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const [showEditSettlementModal, setShowEditSettlementModal] = useState(false)
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([])
  const invalidateUseStaffGetContractSettlement =
    useInvalidateUseStaffGetContractSettlement()

  const { specialConditionIdToClauseNumberMap } = useGetSpecialConditionsInfo(
    campaignId,
    contract?.contractId
  )

  const {
    data: contractSettlement,
    isPending,
    isError,
  } = useStaffGetContractSettlement(
    contract?.contractId || '',
    !!contract?.contractId
  )

  const [settlementInfo, setSettlementInfo] = useState<
    FormCondition | undefined
  >(contractSettlement?.settlementInfo)

  const settlementDueDate = useFormConditionDueDate(settlementInfo)

  const [signboardRemoval, setSignboardRemoval] = useState<
    SignboardRemoval | undefined
  >(contractSettlement?.signboardRemoval)

  const [openRexDisconnectedModal, setOpenRexDisconnectedModal] = useState(false)
  const [rexModalChangedFields, setRexModalChangedFields] = useState<Map<RexDisconnectedModalFields, RexDisconnectedModalFieldValue>>(new Map())

  useEffect(() => {
    setSettlementInfo(contractSettlement?.settlementInfo)
    setSignboardRemoval(contractSettlement?.signboardRemoval)
    setIsLoaded(!isPending)
  }, [contractSettlement, isPending])

  useEffect(() => {
    setValidate(
      !isDirtyEqual(settlementInfo, contractSettlement?.settlementInfo) ||
      !isDirtyEqual(signboardRemoval, contractSettlement?.signboardRemoval)
    )
  }, [settlementInfo, signboardRemoval])

  useEffect(() => {
    if (!openRexDisconnectedModal) {
      setRexModalChangedFields(new Map());
    }
  }, [openRexDisconnectedModal]);

  const {
    staffUpdateContractSettlement,
    staffUpdateContractSettlementPending,
  } = useStaffUpdateContractSettlement({
    onSuccess: () => {},
    onError: () => {},
  })

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaignId || '', campaignId !== undefined);

  const isFormDirty = useMemo(() => {
    return (
      !isDirtyEqual(settlementInfo, contractSettlement?.settlementInfo) ||
      !isDirtyEqual(signboardRemoval, contractSettlement?.signboardRemoval)
    )
  }, [settlementInfo, signboardRemoval, contractSettlement])

  const prevSettlementInfo = useMemo(
    () => contractSettlement?.settlementInfo,
    [contractSettlement]
  )

  const prevSettlementDueDate = useFormConditionDueDate(prevSettlementInfo)
  const currSettlementDueDate = useFormConditionDueDate(settlementInfo)

  const onSave = useCallback(
    async (success: () => void) => {
      if (!contract?.contractId) return

      if (!rexListing && isPendingRexListing === false) {
        setRexModalChangedFields(prev => {
          const newMap = new Map(prev);

          // Compare calculated due dates
          if (!isEqual(prevSettlementDueDate, currSettlementDueDate)) {
            newMap.set(RexDisconnectedModalFields.SettlementDate, currSettlementDueDate?.dueDate?.toLocaleDateString() || '');
          }
          return newMap;
        });

        if (!isEqual(prevSettlementDueDate, currSettlementDueDate)) {
          setOpenRexDisconnectedModal(true);
        }
      }

      const request: StaffUpdateContractSettlementRequest = {
        settlementInfo: settlementInfo,
        signboardRemoval: signboardRemoval,
      }

      await staffUpdateContractSettlement(
        {
          body: request,
          contractId: contract.contractId,
        },
        {
          onSuccess: async (data) => {
            if (data.success) {
              success()
              await invalidateUseStaffGetContractSettlement(contract.contractId)
            } else {
              setSubmitFormErrors(data.errors || [])
              setOpenErrorModal(true)
            }
          },
        }
      )
    },
    [
      settlementInfo,
      signboardRemoval,
      contract?.contractId,
      staffUpdateContractSettlement,
    ]
  )

  if (!isLoaded) {
    return <LoadingState />
  }

  return (
    <ContractSubPage
      key={`SettlementPage${isPending ? 'Loaded' : ''}`}
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={Pages.SETTLEMENT}
      subPages={getContractInformationSubPages()}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={staffUpdateContractSettlementPending}
      isError={isError}
    >
      <SubPagePanel
        key={`SettlementPanel${isPending ? 'Loaded' : ''}`}
        panelTitle="Settlement"
        expandable
      >
        {settlementInfo && (
          <>
            <AgxSpacer
              direction="horizontal"
              gap="spacing-md"
              fill
              padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
            >
              <ConditionDate
                id="settlementInfo"
                condition={settlementInfo}
                specialConditionIdToClauseNumberMap={
                  specialConditionIdToClauseNumberMap ?? {}
                }
              />
            </AgxSpacer>
            <AgxDivider variant="horizontal" />
          </>
        )}
        <AgxSpacer
          direction="horizontal"
          gap="spacing-md"
          fill
          padding={{ top: 'spacing-md', bottom: 'spacing-3xl' }}
          contentAlignment="end"
        >
          <AgxDatePicker
            id="settlementInfo_due_date"
            key={`settlementInfo_due_date_${settlementDueDate?.dueDate}`}
            stretch
            disabled={true}
            optionalText=""
            label="Due Date"
            value={
              settlementDueDate?.dueDate
                ? dayjs(settlementDueDate.dueDate).format('YYYY-MM-DD')
                : ''
            }
          />
          <AgxButton
            id="settlementInfo_edit"
            variant="hollow"
            text="Edit"
            onClick={() => setShowEditSettlementModal(true)}
          />
        </AgxSpacer>
        {showEditSettlementModal && settlementInfo && (
          <EditConditionModal
            id="settlementInfo"
            modalTitle="Edit Settlement Date"
            formCondition={settlementInfo}
            onDateConditions={[
              FormConditionCondition.OnSpecificDate,
              FormConditionCondition.UponSatisfactionOfSpecialCondition,
            ]}
            fromDateConditions={[
              FormConditionCondition.NdaysFromDate,
              FormConditionCondition.UponSatisfactionOfSpecialCondition,
            ]}
            openModal={setShowEditSettlementModal}
            onChange={(updatedCondition) => {
              setSettlementInfo(updatedCondition as FormCondition)
              setShowEditSettlementModal(false)
            }}
          />
        )}
      </SubPagePanel>
      <SubPagePanel
        key={`SignboardRemovalPanel${isPending ? 'Loaded' : ''}`}
        panelTitle="Signboard Removal"
        expandable
      >
        <AgxRadioGroup
          id="signboardRemoval_RemovalType"
          key={`signboardRemoval_RemovalType${isPending ? 'Loaded' : ''}`}
          layout="stacked"
          value={signboardRemoval?.removalType || ''}
          onChange={(val) => {
            if (val === signboardRemoval?.removalType) return
            setSignboardRemoval((prev) => ({
              ...prev,
              removalType: val as SignboardRemovalRemovalType,
            }))
          }}
          options={[
            {
              label: 'Upon settlement',
              value: SignboardRemovalRemovalType.UponSettlement,
            },
            {
              label: 'On Specific Date',
              value: SignboardRemovalRemovalType.OnSpecificDate,
            },
            {
              label: 'No signboard installed',
              value: SignboardRemovalRemovalType.NoSignboardInstalled,
            },
          ]}
          subContent={
            signboardRemoval?.removalType ===
              SignboardRemovalRemovalType.OnSpecificDate
              ? [
                <AgxSpacer
                  direction="vertical"
                  key={`signboardRemoval_specificDate_Column${isPending ? 'Loaded' : ''}`}
                  gap="spacing-md"
                  padding={{
                    top: 'spacing-xl',
                    bottom: 'spacing-xl',
                    left: 'spacing-xl',
                    right: 'spacing-xl',
                  }}
                >
                  <AgxTextInput
                    id="signboardRemoval_specificDate"
                    key={`signboardRemoval_specificDate${isPending ? 'Loaded' : ''}`}
                    onInputValueChange={({ value }) => {
                      setSignboardRemoval((prev) => ({
                        ...prev,
                        specificDate: new Date(value),
                      }))
                    }}
                    required
                    hideOptionalText
                    type="date"
                    defaultValue={
                      signboardRemoval?.specificDate
                        ? dayjs(signboardRemoval?.specificDate).format(
                          'YYYY-MM-DD'
                        )
                        : undefined
                    }
                    errorText={
                      validate && !signboardRemoval?.specificDate
                        ? 'Enter a date'
                        : undefined
                    }
                  />
                </AgxSpacer>,
              ]
              : undefined
          }
        />
      </SubPagePanel>
      {openErrorModal && (
        <ErrorModal
          errorList={submitFormErrors}
          setShowModal={setOpenErrorModal}
        />
      )}
      {openRexDisconnectedModal ? (
        <RexDisconnectedModal
          setIsDialogOpen={setOpenRexDisconnectedModal}
          changedFields={rexModalChangedFields}
        />
      ) : (
        <></>
      )}
    </ContractSubPage>
  )
}

export default Settlement
